import React, { useContext, useState } from 'react';
import {
  Box,
  VStack,
  Icon,
  Avatar,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Image,
  Flex,
  Text,
  Tooltip,
  Divider,
  useColorModeValue,
  IconButton,
  Badge,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Input,
  FormControl,
  FormLabel,
  useToast,
  Drawer,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  useDisclosure,
  useBreakpointValue,
  HStack,
  Collapse
} from '@chakra-ui/react';
import {
  MdDashboard,
  MdPeople,
  MdLibraryBooks,
  MdEvent,
  MdSettings,
  MdLogout,
  MdInsertChart,
  MdHome,
  MdFeedback,
  MdDataExploration,
  MdNotifications,
  MdCyclone,
  MdMenu,
  MdBookmarks,
  MdClose,
  MdSchool,
  MdAssignment,
  MdPayment
} from 'react-icons/md';
import { useNavigate, useLocation } from 'react-router-dom';
import AuthContext from '../services/AuthContext';
import axios from 'axios';
import axiosInstance from '../services/axiosInstance';
import { FiMessageCircle, FiClock } from 'react-icons/fi';

const SimpleIconSidebar = () => {
  const { logout, user } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isChanging, setIsChanging] = useState(false);
  const toast = useToast();

  // Mobile drawer state
  const { isOpen: isDrawerOpen, onOpen: onDrawerOpen, onClose: onDrawerClose } = useDisclosure();

  // Responsive breakpoints
  const isMobile = useBreakpointValue({ base: true, md: false });
  const sidebarWidth = useBreakpointValue({ base: '100%', md: '70px' });
  const showMobileMenu = useBreakpointValue({ base: true, md: false });

  // Platform colors
  const primaryColor = '#F2C919';
  const secondaryColor = '#2C3E50';
  const bgColor = useColorModeValue(secondaryColor, "gray.900");
  const hoverBg = useColorModeValue(`${primaryColor}20`, "yellow.700");
  const activeBg = useColorModeValue(primaryColor, "yellow.600");
  const textColor = useColorModeValue("white", "gray.100");

  const adminMenuItems = [
    { icon: MdPeople, path: '/admin/users', label: 'المستخدمين' },
    { icon: MdSchool, path: '/admin/niveaux', label: 'إدارة المستويات' },
    { icon: MdAssignment, path: '/admin/registrations', label: 'طلبات التسجيل' },
    { icon: MdLibraryBooks, path: '/admin/courses', label: 'الدورات' },
    { icon: MdEvent, path: '/admin/events', label: 'الأحداث' },
    { icon: MdDataExploration, path: '/instructor/absencemanagement', label: 'الغيابات' },
    { icon: MdFeedback, path: '/admin/reclamation', label: 'الشكاوى' },
    { icon: FiMessageCircle, path: '/admin/whatsapp-groups', label: 'مجموعات واتساب' },
    { icon: FiClock, path: '/attendance/control', label: 'رصد الغياب' },
    { icon: MdPayment, path: '/admin/payments', label: 'إدارة المدفوعات' },
    // { icon: MdCyclone, path: '/admin/maylawan', label: 'Maylawan' },
    { icon: MdBookmarks, path: '/admin/student-feedback', label: 'تقييمات الطلاب' },
  ];

  const isActive = (path) => {
    return location.pathname.startsWith(path);
  };

  const handlePasswordChange = async () => {
    if (newPassword !== confirmPassword) {
      toast({
        title: 'خطأ',
        description: 'كلمة المرور الجديدة وتأكيدها غير متطابقين',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setIsChanging(true);
    try {
      const response = await axiosInstance.post('/auth/change-password', {
        currentPassword,
        newPassword
      }, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      toast({
        title: 'تم بنجاح',
        description: 'تم تغيير كلمة المرور بنجاح',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      setIsModalOpen(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      toast({
        title: 'خطأ',
        description: error.response?.data?.message || 'حدث خطأ أثناء تغيير كلمة المرور',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsChanging(false);
    }
  };

  const handleMenuItemClick = (path) => {
    navigate(path);
    if (isMobile) {
      onDrawerClose();
    }
  };

  // Mobile Hamburger Menu Button
  const MobileMenuButton = () => (
    <Box
      position="fixed"
      top="4"
      right="4"
      zIndex="overlay"
      display={{ base: 'block', md: 'none' }}
    >
      <IconButton
        aria-label="فتح القائمة"
        icon={<MdMenu />}
        onClick={onDrawerOpen}
        size="lg"
        bg={bgColor}
        color={textColor}
        _hover={{ bg: hoverBg }}
        borderRadius="xl"
        boxShadow="lg"
        border="2px solid"
        borderColor={primaryColor}
      />
    </Box>
  );

  // Sidebar Content Component (reusable for both desktop and mobile)
  const SidebarContent = ({ onItemClick = () => { } }) => (
    <Flex direction="column" height="100%" justifyContent="space-between" py={4}>
      {/* Top Section */}
      <VStack spacing={6}>
        {/* Logo */}
        <Box p={2}>
          <Image
            src="/assets/logo.png"
            alt="شعار"
            boxSize="40px"
            objectFit="contain"
            fallbackSrc="https://ui-avatars.com/api/?name=C&background=F2C919&color=2C3E50"
            mx="auto"
          />
        </Box>

        {/* Navigation Icons */}
        <VStack spacing={4} width="100%">
          {adminMenuItems.map((item) => (
            <Box key={item.path} width="100%">
              {isMobile ? (
                // Mobile: Show text with icons
                <Button
                  leftIcon={<item.icon />}
                  variant="ghost"
                  size="lg"
                  width="100%"
                  justifyContent="flex-start"
                  color={isActive(item.path) ? secondaryColor : textColor}
                  bg={isActive(item.path) ? activeBg : "transparent"}
                  _hover={{
                    bg: isActive(item.path) ? activeBg : hoverBg,
                    color: isActive(item.path) ? secondaryColor : textColor
                  }}
                  onClick={() => {
                    handleMenuItemClick(item.path);
                    onItemClick();
                  }}
                  borderRadius="xl"
                  fontSize="md"
                  fontWeight="600"
                  py={6}
                  px={4}
                  dir="rtl"
                  minH="48px" // Touch-friendly minimum height
                >
                  {item.label}
                </Button>
              ) : (
                // Desktop: Icon only with tooltip
                <Tooltip
                  label={item.label}
                  placement="left"
                  bg={secondaryColor}
                  color="white"
                  hasArrow
                >
                  <Box
                    position="relative"
                    width="100%"
                    display="flex"
                    justifyContent="center"
                  >
                    <IconButton
                      aria-label={item.label}
                      icon={<item.icon />}
                      variant="ghost"
                      size="lg"
                      color={isActive(item.path) ? secondaryColor : textColor}
                      bg={isActive(item.path) ? activeBg : "transparent"}
                      _hover={{
                        bg: isActive(item.path) ? activeBg : hoverBg,
                        color: isActive(item.path) ? secondaryColor : textColor
                      }}
                      onClick={() => handleMenuItemClick(item.path)}
                      borderRadius="xl"
                      minH="48px"
                      minW="48px"
                    />
                    {isActive(item.path) && (
                      <Box
                        position="absolute"
                        right="0"
                        top="0"
                        bottom="0"
                        width="3px"
                        bg={secondaryColor}
                        borderRadius="full"
                      />
                    )}
                  </Box>
                </Tooltip>
              )}
            </Box>
          ))}
        </VStack>
      </VStack>

      {/* Bottom Section */}
      <VStack spacing={4}>
        {/* Home Button */}
        {isMobile ? (
          <Button
            leftIcon={<MdHome />}
            variant="ghost"
            size="lg"
            width="100%"
            justifyContent="flex-start"
            color={textColor}
            _hover={{ bg: hoverBg }}
            onClick={() => {
              handleMenuItemClick('/');
              onItemClick();
            }}
            borderRadius="xl"
            fontSize="md"
            fontWeight="600"
            py={6}
            px={4}
            dir="rtl"
            minH="48px"
          >
            الصفحة الرئيسية
          </Button>
        ) : (
          <Tooltip label="الصفحة الرئيسية" placement="left" bg={secondaryColor} color="white" hasArrow>
            <IconButton
              aria-label="الصفحة الرئيسية"
              icon={<MdHome />}
              variant="ghost"
              size="lg"
              color={textColor}
              _hover={{ bg: hoverBg }}
              onClick={() => handleMenuItemClick('/')}
              minH="48px"
              minW="48px"
            />
          </Tooltip>
        )}

        {/* User Avatar */}
        <Menu>
          <MenuButton>
            {isMobile ? (
              <HStack
                spacing={3}
                p={3}
                borderRadius="xl"
                _hover={{ bg: hoverBg }}
                cursor="pointer"
                width="100%"
                minH="48px"
              >
                <Avatar
                  size="sm"
                  name={user?.username}
                  bg={primaryColor}
                  color={secondaryColor}
                  fontWeight="bold"
                />
                <Text color={textColor} fontSize="md" fontWeight="600">
                  {user?.username}
                </Text>
              </HStack>
            ) : (
              <Avatar
                size="sm"
                name={user?.username}
                bg={primaryColor}
                color={secondaryColor}
                fontWeight="bold"
                cursor="pointer"
              />
            )}
          </MenuButton>
          <MenuList
            dir="rtl"
            bg="white"
            color="gray.800"
            borderColor="gray.200"
            minWidth="150px"
            borderRadius="xl"
            boxShadow="xl"
          >
            <MenuItem
              icon={<MdSettings />}
              onClick={() => setIsModalOpen(true)}
              borderRadius="lg"
              mx={2}
              my={1}
              minH="44px"
              fontSize="md"
            >
              تغيير كلمة المرور
            </MenuItem>
            <MenuItem
              icon={<MdLogout />}
              onClick={logout}
              _hover={{ bg: "red.50", color: "red.600" }}
              borderRadius="lg"
              mx={2}
              my={1}
              minH="44px"
              fontSize="md"
            >
              تسجيل الخروج
            </MenuItem>
          </MenuList>
        </Menu>
      </VStack>
    </Flex>
  );

  return (
    <>
      {/* Mobile Menu Button */}
      <MobileMenuButton />

      {/* Desktop Sidebar */}
      <Box
        position="fixed"
        top="0"
        right="0"
        width={sidebarWidth}
        height="100vh"
        bg={bgColor}
        color={textColor}
        zIndex="sticky"
        borderLeft="3px solid"
        borderLeftColor={primaryColor}
        dir="rtl"
        display={{ base: 'none', md: 'block' }}
      >
        <SidebarContent />
      </Box>

      {/* Mobile Drawer */}
      <Drawer
        isOpen={isDrawerOpen}
        placement="right"
        onClose={onDrawerClose}
        size="xs"
      >
        <DrawerOverlay />
        <DrawerContent
          bg={bgColor}
          color={textColor}
          dir="rtl"
        >
          <DrawerCloseButton
            size="lg"
            color={textColor}
            _hover={{ bg: hoverBg }}
            borderRadius="xl"
            top="4"
            right="4"
          />
          <DrawerHeader borderBottomWidth="1px" borderBottomColor={primaryColor}>
            <HStack spacing={3}>
              <Image
                src="/assets/logo.png"
                alt="شعار"
                boxSize="32px"
                objectFit="contain"
                fallbackSrc="https://ui-avatars.com/api/?name=C&background=F2C919&color=2C3E50"
              />
              <Text fontSize="lg" fontWeight="bold">
                لوحة الإدارة
              </Text>
            </HStack>
          </DrawerHeader>

          <DrawerBody p={4}>
            <SidebarContent onItemClick={onDrawerClose} />
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      {/* Password Change Modal */}
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} size={{ base: 'full', md: 'md' }}>
        <ModalOverlay />
        <ModalContent
          dir="rtl"
          mx={{ base: 0, md: 4 }}
          my={{ base: 0, md: 16 }}
          borderRadius={{ base: 0, md: 'xl' }}
        >
          <ModalHeader fontSize="xl" fontWeight="bold">
            تغيير كلمة المرور
          </ModalHeader>
          <ModalCloseButton
            size="lg"
            borderRadius="xl"
          />
          <ModalBody pb={6}>
            <VStack spacing={4}>
              <FormControl>
                <FormLabel fontSize="md" fontWeight="600">
                  كلمة المرور الحالية
                </FormLabel>
                <Input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="أدخل كلمة المرور الحالية"
                  size="lg"
                  borderRadius="xl"
                  _focus={{
                    borderColor: primaryColor,
                    boxShadow: `0 0 0 1px ${primaryColor}`
                  }}
                />
              </FormControl>

              <FormControl>
                <FormLabel fontSize="md" fontWeight="600">
                  كلمة المرور الجديدة
                </FormLabel>
                <Input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="أدخل كلمة المرور الجديدة"
                  size="lg"
                  borderRadius="xl"
                  _focus={{
                    borderColor: primaryColor,
                    boxShadow: `0 0 0 1px ${primaryColor}`
                  }}
                />
              </FormControl>

              <FormControl>
                <FormLabel fontSize="md" fontWeight="600">
                  تأكيد كلمة المرور الجديدة
                </FormLabel>
                <Input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="أعد إدخال كلمة المرور الجديدة"
                  size="lg"
                  borderRadius="xl"
                  _focus={{
                    borderColor: primaryColor,
                    boxShadow: `0 0 0 1px ${primaryColor}`
                  }}
                />
              </FormControl>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <VStack spacing={3} width="100%">
              <Button
                colorScheme="yellow"
                onClick={handlePasswordChange}
                isLoading={isChanging}
                loadingText="جاري التغيير..."
                size="lg"
                width="100%"
                borderRadius="xl"
                fontSize="md"
                fontWeight="600"
              >
                حفظ التغييرات
              </Button>
              <Button
                onClick={() => setIsModalOpen(false)}
                variant="ghost"
                size="lg"
                width="100%"
                borderRadius="xl"
                fontSize="md"
                fontWeight="600"
              >
                إلغاء
              </Button>
            </VStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default SimpleIconSidebar;

